import { Box } from '@mui/material';
import React from 'react';
import SpeechBubblePortal from '../SpeechBubblePortal';
import { StyledTypography } from '../styles/styles';
import ContentDiffComparison from './ContentDiffComparison';
import FullContentDiffViewerControls from './FullContentDiffViewerControls';
import { FullContentDiffViewerProps } from './fullContentDiffViewerTypes';
import { useFullContentDiffViewer } from './useFullContentDiffViewer';
import VersionHistorySidebar from './VersionHistorySidebar';

const FullContentDiffViewer: React.FC<FullContentDiffViewerProps> = (props) => {
  const { tone = 'tone1', modelType, modelId, hideVersionHistory = false, currentUser } = props;

  const viewer = useFullContentDiffViewer(props);

  if (!viewer.contentToCompare?.originalContent || !viewer.contentToCompare?.newContent) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <StyledTypography color="error">Missing content data for comparison</StyledTypography>
      </Box>
    );
  }

  return (
    <>
      {!hideVersionHistory && modelType && modelId && (
        <VersionHistorySidebar
          isOpen={viewer.sidebarOpen}
          onClose={() => viewer.setSidebarOpen(false)}
          modelType={modelType}
          modelId={modelId}
          onVersionSelect={viewer.handleVersionSelect}
          onVersionsLoaded={viewer.setVersions}
          selectedVersionId={viewer.getCurrentVersionId() || undefined}
          tone={tone}
        />
      )}

      <FullContentDiffViewerControls
        tone={tone}
        comparisonMode={viewer.comparisonMode}
        onComparisonModeChange={viewer.setComparisonMode}
        loadingLatestDraft={viewer.loadingLatestDraft}
        loadingPublished={viewer.loadingPublished}
        selectedVersion={viewer.selectedVersion}
        isSelectedVersionLatest={viewer.isSelectedVersionLatest}
        sidebarOpen={viewer.sidebarOpen}
        onToggleSidebar={() => viewer.setSidebarOpen(!viewer.sidebarOpen)}
        historyButtonRef={viewer.historyButtonRef}
        onBackToOriginal={viewer.handleBackToOriginal}
      />

      <Box sx={{ position: 'relative' }}>
        <ContentDiffComparison
          originalContent={viewer.currentFullContent.originalContent}
          newContent={viewer.currentFullContent.newContent}
          tone={tone}
          renderContentItem={viewer.renderContentItem}
          currentUser={currentUser || undefined}
        />
      </Box>

      <SpeechBubblePortal
        show={viewer.showSpeechBubble}
        message="Click the history icon to view all previous versions of this content. Select any version to compare it with the current version and see what changed."
        targetRef={viewer.historyButtonRef}
        onClose={viewer.handleSpeechBubbleClose}
        tone="tone4"
        dismissable={true}
        placement="right"
      />
    </>
  );
};

export default FullContentDiffViewer;
